// 019 — Cuidados Post-Anestésicos (Fase 1B, ver PLAN.md). Reemplaza el stub
// anterior (Aldrete suelto + signos vitales sueltos vía LOINC, ~15% del
// spec real).
//
// **Tanda 15 de 2**: Bloque C (entrega-recepción al ingreso a UCPA) +
// Bloque D (monitorización — reusa el gráfico de signos vitales de la
// Tanda 12, `signosVitalesAnestesia`, sin cambios) + Bloque H (condición de
// egreso de UCPA).
// **Tanda 16 de 2 (cierra 019 y toda la Fase 1B)**: Bloques B/E/F/G/I
// (`bloquesRestantes019Datos.ts`/`BloquesRestantes019.tsx`) — único recurso
// FHIR real nuevo de esta tanda: 1 `Condition` (diagnóstico del bloque B),
// mismo patrón sin rollback que los diagnósticos de 018A.
//
// Las 6 "escalas" de recuperación anestésica (Aldrete/Bromage/Ramsay/EVA/
// Steward/Otro) se capturan 3 veces — una por momento (ingreso en C,
// reevaluación en D, egreso en H) — vía el componente compartido
// `EscalasRecuperacionAnestesica` (`escalasRecuperacionAnestesica.ts` tiene
// el detalle de por qué cada momento es un `Observation` aparte, no un
// único registro que se sobrescribe).
//
// Los 7 parámetros continuos del gráfico de D (TAS/TAD/TAM/FC/temperatura/
// PVC/SatO2) tampoco se persisten como `Observation` acá — mismo criterio
// que 018A (decisión de diseño de la Tanda 12: sin consumidor real hoy).
// Esto REEMPLAZA la captura de signos vitales del stub anterior (`Observation`
// LOINC sueltos vía `SIGNOS_VITALES`), que no tenía consumidor propio
// tampoco — no es una regresión de datos, es el mismo mecanismo ya usado en
// 018A para el mismo tipo de dato.
import type { Observation } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DatosSignosVitalesPdf } from '@logiacare/hce-pdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { FormularioDiscretoProps, Par } from './index';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { datosSignosVitalesPdf, GraficoSignosVitalesAnestesia, type EventoAnestesia, type MedicionPuntual } from './signosVitalesAnestesia';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { EscalasRecuperacionAnestesica } from './EscalasRecuperacionAnestesica';
import { estadoInicialEscalas, observacionesDeEscalas, paresDeEscalas, type EstadoEscalasRecuperacion } from './escalasRecuperacionAnestesicaDatos';
import { observacionesDeReevaluaciones, paresDeReevaluaciones, reevaluacionVacia, TOPE_REEVALUACIONES, type Reevaluacion } from './reevaluaciones019Datos';
import { MINUTO_MAX_019 } from './signosVitalesAnestesia/tipos';
import { Trash2 } from 'lucide-react';
import { codeDeDiagnostico, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie } from './camposSimplesDatos';
import { Bloque019B, Bloque019EFGI } from './BloquesRestantes019';
import { estadoInicialBloquesRestantes019, paresDeBloquesRestantes019 } from './bloquesRestantes019Datos';

const CODIGO = '019';

const EXTUBADO_INTUBADO_OPCIONES = ['extubado', 'intubado'] as const;
type ExtubadoIntubado = (typeof EXTUBADO_INTUBADO_OPCIONES)[number];

const CONDUCIDO_A_OPCIONES = ['hospitalizacion', 'uci', 'criticos-emergencia', 'domicilio', 'morgue'] as const;
type ConducidoA = (typeof CONDUCIDO_A_OPCIONES)[number];

function conPrefijo(pares: readonly Par[], prefijo: string): Par[] {
  return pares.map((p) => ({ pregunta: `${prefijo} · ${p.pregunta}`, respuesta: p.respuesta }));
}

const DIAGNOSTICO_VACIO: DiagnosticoCie10Valor = { texto: '', cie10: null };

export function Formulario019({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f019.titulo');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Recuperación postanestésica (UCPA): el papel no trae servicio.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: 'Recuperación postanestésica' });

  // B. Procedimiento.
  const [diagnostico, setDiagnostico] = useState<DiagnosticoCie10Valor>(DIAGNOSTICO_VACIO);
  const [bloques, setBloques] = useState(() => estadoInicialBloquesRestantes019());

  // C. Entrega-recepción al ingreso a UCPA.
  const [fechaIngreso, setFechaIngreso] = useState('');
  const [horaIngreso, setHoraIngreso] = useState('');
  const [escalasIngreso, setEscalasIngreso] = useState<EstadoEscalasRecuperacion>(() => estadoInicialEscalas());
  const [entregaIngreso, setEntregaIngreso] = useState('');
  const [recibeIngreso, setRecibeIngreso] = useState('');

  // D. Monitorización.
  const [mediciones, setMediciones] = useState<readonly MedicionPuntual[]>([]);
  const [eventos, setEventos] = useState<readonly EventoAnestesia[]>([]);
  // El papel trae 6 reevaluaciones (una por bloque horario); se arranca con 1 y se agregan hasta 6.
  const [reevaluaciones, setReevaluaciones] = useState<readonly Reevaluacion[]>(() => [reevaluacionVacia()]);

  // H. Condición de egreso de UCPA.
  const [taEgreso, setTaEgreso] = useState('');
  const [fcEgreso, setFcEgreso] = useState('');
  const [frEgreso, setFrEgreso] = useState('');
  const [sato2Egreso, setSato2Egreso] = useState('');
  const [temperaturaEgreso, setTemperaturaEgreso] = useState('');
  const [fechaEgreso, setFechaEgreso] = useState('');
  const [horaEgreso, setHoraEgreso] = useState('');
  const [escalasEgreso, setEscalasEgreso] = useState<EstadoEscalasRecuperacion>(() => estadoInicialEscalas());
  const [entregaEgreso, setEntregaEgreso] = useState('');
  const [recibeEgreso, setRecibeEgreso] = useState('');
  const [extubadoIntubado, setExtubadoIntubado] = useState<ExtubadoIntubado | ''>('');
  const [conducidoA, setConducidoA] = useState<ConducidoA | ''>('');

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const ahora = new Date().toISOString();

      const pares: Par[] = [
        { pregunta: t('formularios.f019.bloqueC.fecha'), respuesta: fechaIngreso.trim() },
        { pregunta: t('formularios.f019.bloqueC.hora'), respuesta: horaIngreso.trim() },
        ...conPrefijo(paresDeEscalas(t, 'formularios.escalasRecuperacion', escalasIngreso), t('formularios.f019.bloqueC.titulo')),
        { pregunta: t('formularios.f019.bloqueC.entrega'), respuesta: entregaIngreso.trim() },
        { pregunta: t('formularios.f019.bloqueC.recibe'), respuesta: recibeIngreso.trim() },
        ...paresDeReevaluaciones(t, 'formularios.escalasRecuperacion', reevaluaciones),
        {
          pregunta: t('formularios.f019.bloqueH.constantesEgreso'),
          respuesta: [taEgreso, fcEgreso, frEgreso, sato2Egreso, temperaturaEgreso].filter((v) => v.trim() !== '').join(' | '),
        },
        { pregunta: t('formularios.f019.bloqueH.fecha'), respuesta: fechaEgreso.trim() },
        { pregunta: t('formularios.f019.bloqueH.hora'), respuesta: horaEgreso.trim() },
        ...conPrefijo(paresDeEscalas(t, 'formularios.escalasRecuperacion', escalasEgreso), t('formularios.f019.bloqueH.titulo')),
        { pregunta: t('formularios.f019.bloqueH.entrega'), respuesta: entregaEgreso.trim() },
        { pregunta: t('formularios.f019.bloqueH.recibe'), respuesta: recibeEgreso.trim() },
        { pregunta: t('formularios.f019.bloqueH.extubadoIntubado'), respuesta: extubadoIntubado ? t(`formularios.f019.bloqueH.extubadoIntubadoOpciones.${extubadoIntubado}`) : '' },
        { pregunta: t('formularios.f019.bloqueH.conducidoA'), respuesta: conducidoA ? t(`formularios.f019.bloqueH.conducidoAOpciones.${conducidoA}`) : '' },
      ].filter((p) => p.respuesta.trim() !== '');

      pares.push(...paresDeBloquesRestantes019(t, bloques));

      const observaciones: Observation[] = [
        ...observacionesDeEscalas(subject, encounter, 'ingreso', escalasIngreso, ahora),
        ...observacionesDeReevaluaciones(subject, encounter, reevaluaciones, ahora),
        ...observacionesDeEscalas(subject, encounter, 'egreso', escalasEgreso, ahora),
      ];
      observaciones.forEach((o) => lote.crear(o));

      if (diagnostico.texto.trim()) {
        lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(diagnostico), recordedDate: ahora });
        pares.push({ pregunta: t('formularios.f019.bloqueB.diagnostico'), respuesta: conCodigoCie(diagnostico) });
      }

      const diagramaSignosVitales: DatosSignosVitalesPdf = datosSignosVitalesPdf(mediciones, eventos);

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp, undefined, undefined, undefined, diagramaSignosVitales);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque019B estado={bloques} onChange={setBloques} diagnostico={diagnostico} onChangeDiagnostico={setDiagnostico} />

      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f019.bloqueC.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-c-fecha">{t('formularios.f019.bloqueC.fecha')}</Label>
            <Input id="f019-c-fecha" type="date" value={fechaIngreso} onChange={(e) => setFechaIngreso(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-c-hora">{t('formularios.f019.bloqueC.hora')}</Label>
            <Input id="f019-c-hora" placeholder="hh:mm" value={horaIngreso} onChange={(e) => setHoraIngreso(e.target.value)} />
          </div>
        </div>
        <EscalasRecuperacionAnestesica titulo={t('formularios.f019.bloqueC.escalasTitulo')} prefijoId="f019-c" estado={escalasIngreso} onChange={setEscalasIngreso} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CampoDictado id="f019-c-entrega" etiqueta={t('formularios.f019.bloqueC.entrega')} valor={entregaIngreso} onChange={setEntregaIngreso} />
          <CampoDictado id="f019-c-recibe" etiqueta={t('formularios.f019.bloqueC.recibe')} valor={recibeIngreso} onChange={setRecibeIngreso} />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f019.bloqueD.titulo')}</h4>
        <GraficoSignosVitalesAnestesia mediciones={mediciones} onChangeMediciones={setMediciones} eventos={eventos} onChangeEventos={setEventos} minutoMax={MINUTO_MAX_019} />
        {reevaluaciones.map((r, i) => (
          <div key={r.id} className="flex flex-col gap-2 rounded-md border border-border p-3">
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor={`f019-d-${r.id}-hora`}>{`${t('formularios.f019.bloqueD.escalasTitulo')} ${i + 1} · ${t('formularios.f019.bloqueD.hora')}`}</Label>
                <Input id={`f019-d-${r.id}-hora`} placeholder="hh:mm" className="w-32" value={r.hora} onChange={(e) => setReevaluaciones((prev) => prev.map((x) => (x.id === r.id ? { ...x, hora: e.target.value } : x)))} />
              </div>
              {reevaluaciones.length > 1 && (
                <button type="button" className="pb-2" aria-label={t('formularios.f019.bloqueD.quitarReevaluacion')} onClick={() => setReevaluaciones((prev) => prev.filter((x) => x.id !== r.id))}>
                  <Trash2 className="size-4 text-hce-muted" />
                </button>
              )}
            </div>
            <EscalasRecuperacionAnestesica
              titulo={`${t('formularios.f019.bloqueD.escalasTitulo')} ${i + 1}`}
              prefijoId={`f019-d-${r.id}`}
              estado={r.escalas}
              onChange={(escalas) => setReevaluaciones((prev) => prev.map((x) => (x.id === r.id ? { ...x, escalas } : x)))}
            />
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={reevaluaciones.length >= TOPE_REEVALUACIONES} onClick={() => setReevaluaciones((prev) => [...prev, reevaluacionVacia()])}>
          {t('formularios.f019.bloqueD.agregarReevaluacion')}
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f019.bloqueH.titulo')}</h4>
        <Label>{t('formularios.f019.bloqueH.constantesEgreso')}</Label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-ta">{t('formularios.f019.bloqueH.ta')}</Label>
            <Input id="f019-h-ta" value={taEgreso} onChange={(e) => setTaEgreso(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-fc">{t('formularios.f019.bloqueH.fc')}</Label>
            <Input id="f019-h-fc" value={fcEgreso} onChange={(e) => setFcEgreso(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-fr">{t('formularios.f019.bloqueH.fr')}</Label>
            <Input id="f019-h-fr" value={frEgreso} onChange={(e) => setFrEgreso(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-sato2">{t('formularios.f019.bloqueH.sato2')}</Label>
            <Input id="f019-h-sato2" value={sato2Egreso} onChange={(e) => setSato2Egreso(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-temperatura">{t('formularios.f019.bloqueH.temperatura')}</Label>
            <Input id="f019-h-temperatura" value={temperaturaEgreso} onChange={(e) => setTemperaturaEgreso(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-fecha">{t('formularios.f019.bloqueH.fecha')}</Label>
            <Input id="f019-h-fecha" type="date" value={fechaEgreso} onChange={(e) => setFechaEgreso(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-hora">{t('formularios.f019.bloqueH.hora')}</Label>
            <Input id="f019-h-hora" placeholder="hh:mm" value={horaEgreso} onChange={(e) => setHoraEgreso(e.target.value)} />
          </div>
        </div>
        <EscalasRecuperacionAnestesica titulo={t('formularios.f019.bloqueH.escalasTitulo')} prefijoId="f019-h" estado={escalasEgreso} onChange={setEscalasEgreso} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CampoDictado id="f019-h-entrega" etiqueta={t('formularios.f019.bloqueH.entrega')} valor={entregaEgreso} onChange={setEntregaEgreso} />
          <CampoDictado id="f019-h-recibe" etiqueta={t('formularios.f019.bloqueH.recibe')} valor={recibeEgreso} onChange={setRecibeEgreso} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-extubado-intubado">{t('formularios.f019.bloqueH.extubadoIntubado')}</Label>
            <Select value={extubadoIntubado || undefined} onValueChange={(v) => setExtubadoIntubado(v as ExtubadoIntubado)}>
              <SelectTrigger id="f019-h-extubado-intubado">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXTUBADO_INTUBADO_OPCIONES.map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(`formularios.f019.bloqueH.extubadoIntubadoOpciones.${op}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="f019-h-conducido-a">{t('formularios.f019.bloqueH.conducidoA')}</Label>
            <Select value={conducidoA || undefined} onValueChange={(v) => setConducidoA(v as ConducidoA)}>
              <SelectTrigger id="f019-h-conducido-a">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDUCIDO_A_OPCIONES.map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(`formularios.f019.bloqueH.conducidoAOpciones.${op}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Bloque019EFGI estado={bloques} onChange={setBloques} />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
